import { IPublicSession, getDefaultCarSelectionMethod } from 'state/session';
import { State } from 'utils/state';
import { fullScreenAutomation } from './fullScreen';
import { syncToServer } from './syncToServer';
import { endSessionCallback } from './endSession';
import { initCarImagesAutoUpdate, initCurrentImageBridge } from './carImages';
import { experienceSwitchingSanitation } from './experience';
import { deviceSelectionSanitation } from './deviceSelection';
import { initOutdatedVehicleEnforcement } from './outdatedVehicle';
import { initCustomer3DUnavailableNotice } from './capacityCutoff';
import { initSessionTimeout } from './timeout';
import { endScheduledSessionCallback } from './schedule';
import { tenantConfig } from 'utils/tenant';
import { initImageSelectionSessionReset } from 'utils/sync/imageSelection';
import type { SessionState } from './computed';

export function initEvents(state: SessionState) {
  syncToServer(state);
  fullScreenAutomation(state);
  endSessionCallback(state);
  initCarImagesAutoUpdate(state);
  initCurrentImageBridge(state);
  initImageSelectionSessionReset(state);
  experienceSwitchingSanitation(state);
  deviceSelectionSanitation(state);
  initOutdatedVehicleEnforcement(state);
  initCustomer3DUnavailableNotice(state);
  initSessionTimeoutEvents(state);
  endScheduledSessionCallback(state);
  initCarSelectionMethod(state);

  // In local dev (localhost only), the WebSocket signaling service isn't available.
  // Use BroadcastChannel for real-time cross-tab session sync instead.
  // Guard with hostname check to prevent loading on deployed dev environments
  // where import.meta.env.DEV can be true due to CI build configuration.
  // Also skip when API_URL points to a remote server (e.g. E2E tests against dev API)
  // since the real signaling service is available there.
  const apiUrl = process.env.API_URL ?? '';
  const isRemoteApi = apiUrl.startsWith('http') && !apiUrl.includes('localhost') && !apiUrl.includes('127.0.0.1');
  const isLocalDevServer =
    import.meta.env.DEV &&
    !isRemoteApi &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (isLocalDevServer) {
    import('./localSync')
      .then(({ initLocalSessionSync }) => {
        initLocalSessionSync(state);
        console.info('[Local Sync] Loaded');
      })
      .catch((err) => console.error('[Local Sync] Failed to load:', err));
  }
}

/**
 * Initialize default car selection method based on tenant configuration
 * Listens for tenant config to change before setting the default
 */
function initCarSelectionMethod(state: State<IPublicSession>) {
  const setDefaultMethod = () => {
    const defaultMethod = getDefaultCarSelectionMethod();
    if (defaultMethod) {
      state.setValues({ carSelectionMethod: defaultMethod });
    }
  };

  // Listen for tenant config flags to be loaded
  // Defensive check for test environments where tenantConfig might be mocked
  const config = tenantConfig();
  if (config && typeof config.on === 'function') {
    config.on('change', (changes) => {
      // Don't override if user has already selected a method
      if (state.get('carSelectionMethod')) {
        return;
      }
      if (changes.includes('config')) {
        setDefaultMethod();
      }
    });
  }

  // Reset to default on session end
  state.on('change', (changes) => {
    if (changes.includes('endedAt')) {
      setDefaultMethod();
    }
  });
}

/**
 * Initialize session timeout events
 * Starts timeout monitoring when a session starts and stops it when session ends
 */
function initSessionTimeoutEvents(state: State<IPublicSession>) {
  // Start timeout monitoring when session starts
  state.on('session:start', () => {
    const timeoutAgent = initSessionTimeout();
    timeoutAgent.start();
  });

  // Start timeout monitoring when session resumes
  state.on('session:resume', () => {
    const timeoutAgent = initSessionTimeout();
    timeoutAgent.start();
  });

  // Stop timeout monitoring when session ends
  state.on('session:end', () => {
    const timeoutAgent = initSessionTimeout();
    timeoutAgent.stop();
  });
}
